import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_pressnet/artifacts/nmfi6tck_Choluteca_net3-BLANCO.png";

const navLinks = [
  { name: "Inicio", href: "/" },
  { name: "Noticias", href: "/noticias" },
  { name: "Deportes", href: "/deportes" },
  { name: "Eventos", href: "/eventos" },
  {
    name: "Explorar",
    children: [
      { name: "Choluteca", href: "/choluteca" },
      { name: "Honduras", href: "/honduras" },
      { name: "Destinos", href: "/destinos" },
      { name: "Artistas", href: "/artistas" },
      { name: "Directorio", href: "/directorio" },
      { name: "En Vivo", href: "/en-vivo" },
      { name: "Mapa", href: "/mapa" },
      { name: "Emprendedores", href: "/emprendedores" },
    ],
  },
  { name: "Galerías", href: "/galerias" },
  // { name: "Antes y Ahora", href: "/antes-ahora" },  // Temporalmente oculto
  // { name: "Guías", href: "/guias" },  // Temporalmente oculto
  { name: "Videos", href: "/videos" },
  { name: "Tienda", href: "/tienda" },
  { name: "Marketplace", href: "/marketplace" },
  { name: "Comparte tu Contenido", href: "/compartir-contenido" },
  { name: "Publicidad", href: "/publicidad" },
];

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  const isActive = (href) => {
    if (href === "/") return location.pathname === "/";
    return location.pathname.startsWith(href);
  };

  return (
    <header
      className="fixed top-0 w-full z-50 bg-white/90 backdrop-blur-xl border-b border-gray-100/50 transition-all duration-300"
      data-testid="main-header"
    >
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20 md:h-24">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0" data-testid="logo-link">
            <img
              src={LOGO_URL}
              alt="Choluteca.net"
              className="h-14 sm:h-16 md:h-20 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-1">
            {navLinks.map((link) =>
              link.children ? (
                <DropdownMenu key={link.name}>
                  <DropdownMenuTrigger asChild>
                    <button
                      className="flex items-center px-4 py-2 text-sm font-medium text-gray-600 hover:text-primary transition-colors"
                      data-testid={`nav-${link.name.toLowerCase()}`}
                    >
                      {link.name}
                      <ChevronDown className="ml-1 h-4 w-4" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-48">
                    {link.children.map((child) => (
                      <DropdownMenuItem key={child.name} asChild>
                        <Link
                          to={child.href}
                          className="w-full"
                          data-testid={`nav-${child.name.toLowerCase()}`}
                        >
                          {child.name}
                        </Link>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Link
                  key={link.name}
                  to={link.href}
                  className={`px-4 py-2 text-sm font-medium transition-colors relative ${
                    isActive(link.href)
                      ? "text-primary"
                      : "text-gray-600 hover:text-primary"
                  }`}
                  data-testid={`nav-${link.name.toLowerCase()}`}
                >
                  {link.name}
                  {isActive(link.href) && (
                    <span className="absolute bottom-0 left-4 right-4 h-0.5 bg-primary rounded-full" />
                  )}
                </Link>
              )
            )}
          </div>

          {/* CTA Button */}
          <div className="hidden md:block">
            <Link to="/contacto">
              <Button
                className="bg-primary hover:bg-primary/90 text-white rounded-full px-6"
                data-testid="contact-cta-btn"
              >
                Contáctanos
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? (
              <X className="h-6 w-6 text-gray-600" />
            ) : (
              <Menu className="h-6 w-6 text-gray-600" />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-gray-100">
            <div className="flex flex-col space-y-1">
              {navLinks.map((link) =>
                link.children ? (
                  <div key={link.name} className="py-2">
                    <p className="px-4 text-sm font-medium text-gray-400 uppercase tracking-wider">
                      {link.name}
                    </p>
                    <div className="mt-2 space-y-1">
                      {link.children.map((child) => (
                        <Link
                          key={child.name}
                          to={child.href}
                          className={`block px-6 py-2 text-sm ${
                            isActive(child.href)
                              ? "text-primary font-medium"
                              : "text-gray-600"
                          }`}
                          onClick={() => setMobileMenuOpen(false)}
                          data-testid={`mobile-nav-${child.name.toLowerCase()}`}
                        >
                          {child.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                ) : (
                  <Link
                    key={link.name}
                    to={link.href}
                    className={`block px-4 py-2 text-sm ${
                      isActive(link.href)
                        ? "text-primary font-medium"
                        : "text-gray-600"
                    }`}
                    onClick={() => setMobileMenuOpen(false)}
                    data-testid={`mobile-nav-${link.name.toLowerCase()}`}
                  >
                    {link.name}
                  </Link>
                )
              )}
              <div className="px-4 pt-4">
                <Link to="/contacto" onClick={() => setMobileMenuOpen(false)}>
                  <Button className="w-full bg-primary hover:bg-primary/90 text-white rounded-full">
                    Contáctanos
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}
